import React, { useState, useEffect } from 'react';
import { Star, Send, MessageCircle, ChevronDown, ChevronUp, Sparkles, Quote } from 'lucide-react';
import { db, auth } from '../firebase/config';
import {
  collection,
  addDoc,
  getDocs,
  query,
  orderBy,
  where,
} from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import AlertModal from './AlertModal';
import { useAlert } from '../hooks/UseAlert';
import UseUserData from '../hooks/UseUserData';

//SOUS-COMPOSANT : affichage des étoiles
const StarRating = ({ value, onChange, readonly = false, size = 'md' }) => {
  const [hovered, setHovered] = useState(0);
  const sz = size === 'lg' ? 'w-9 h-9' : size === 'sm' ? 'w-5 h-5' : 'w-7 h-7';

  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((star) => {
        const filled = star <= (readonly ? value : hovered || value);
        return (
          <button
            key={star}
            type="button"
            onClick={() => !readonly && onChange && onChange(star)}
            onMouseEnter={() => !readonly && setHovered(star)}
            onMouseLeave={() => !readonly && setHovered(0)}
            disabled={readonly}
            className={`transition-all duration-150 ${readonly ? 'cursor-default' : 'cursor-pointer hover:scale-125'}`}
          >
            <Star
              className={`${sz} transition-colors duration-150 ${
                filled ? 'fill-amber-400 text-amber-400' : 'text-gray-300'
              }`}
            />
          </button>
        );
      })}
    </div>
  );
};


//SOUS-COMPOSANT : une carte d'avis
const ReviewCard = ({ review }) => {
  const date = review.createdAt
    ? new Date(review.createdAt).toLocaleDateString('fr-FR', {
        day: 'numeric', month: 'long', year: 'numeric',
      })
    : '';

  return (
    <div className="relative bg-white rounded-2xl p-6 shadow-md hover:shadow-xl transition-shadow duration-300 border border-purple-50 flex flex-col gap-4">
      {/* Guillemet décoratif */}
      <Quote className="absolute top-4 right-4 w-8 h-8 text-purple-100" />

      {/* En-tête */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          {/* Avatar initiales */}
          <div className="w-11 h-11 rounded-full bg-gradient-to-br from-purple-400 to-pink-400 flex items-center justify-center text-white font-semibold text-sm flex-shrink-0 shadow-md">
            {review.userName
              ? review.userName.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase()
              : '?'}
          </div>
          <div>
            <p className="font-semibold text-purple-900 leading-tight">{review.userName || 'Client anonyme'}</p>
            {review.serviceName && (
              <p className="text-xs text-purple-500 mt-0.5">{review.serviceName}</p>
            )}
          </div>
        </div>
        <StarRating value={review.rating} readonly size="sm" />
      </div>

      {/* Texte de l'avis */}
      {review.comment && (
        <p className="text-gray-600 text-sm leading-relaxed flex-1 italic">
          "{review.comment}"
        </p>
      )}

      {/* Date */}
      <p className="text-xs text-gray-400 mt-auto">{date}</p>
    </div>
  );
};

//COMPOSANT PRINCIPAL : système d'avis clients
const ReviewSystem = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userHasReviewed, setUserHasReviewed] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const [form, setForm] = useState({
    rating: 0,
    comment: '',
    serviceName: '',
  });
  const [formError, setFormError] = useState('');

  const REVIEWS_PREVIEW = 3;

  const { user, userData } = UseUserData();

  /* — Charger les avis */
  useEffect(() => {
    loadReviews();
  }, []);

  /* — Vérifier si l'utilisateur a déjà laissé un avis */
  useEffect(() => {
    if (user && reviews.length) {
      setUserHasReviewed(reviews.some(r => r.userId === user.uid));
    }
  }, [user, reviews]);

  const loadReviews = async () => {
    setLoading(true);
    try {
      const q = query(
        collection(db, 'reviews'),
        orderBy('createdAt', 'desc')
      );
      const snap = await getDocs(q);
      const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setReviews(data);
    } catch (e) {
      console.error('Erreur chargement avis:', e);
    } finally {
      setLoading(false);
    }
  };

  /* — Calcul de la moyenne */
  const average = reviews.length
    ? (reviews.reduce((sum, r) => sum + (r.rating || 0), 0) / reviews.length).toFixed(1)
    : null;

  /* — Distribution des notes */
  const ratingCounts = [5, 4, 3, 2, 1].map(star => ({
    star,
    count: reviews.filter(r => r.rating === star).length,
  }));

  /* — Soumission du formulaire */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.rating === 0) { setFormError('Veuillez sélectionner une note'); return; }
    if (!form.comment.trim()) { setFormError('Veuillez écrire un commentaire'); return; }

    setSubmitting(true);
    setFormError('');

    try {
      const userName = userData?.name || user?.email?.split('@')[0] || 'Client anonyme';

      await addDoc(collection(db, 'reviews'), {
        rating: form.rating,
        comment: form.comment.trim(),
        serviceName: form.serviceName.trim(),
        userName,
        userId: user?.uid || null,
        userEmail: user?.email || null,
        createdAt: new Date().toISOString(),
        approved: true,
      });

      setSubmitSuccess(true);
      setForm({ rating: 0, comment: '', serviceName: '' });
      setShowForm(false);
      await loadReviews();

      setTimeout(() => setSubmitSuccess(false), 4000);
    } catch (err) {
      console.error('Erreur soumission avis:', err);
      setFormError('Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setSubmitting(false);
    }
  };

  const visibleReviews = showAll ? reviews : reviews.slice(0, REVIEWS_PREVIEW);
  const { alertConfig, closeAlert, warning, error, success } = useAlert();

  return (
    <>
    <AlertModal
        isOpen={alertConfig.isOpen}
        onClose={closeAlert}
        type={alertConfig.type}
        title={alertConfig.title}
        message={alertConfig.message}
        confirmText={alertConfig.confirmText}
        onConfirm={alertConfig.onConfirm}
        showCancel={alertConfig.showCancel}
        cancelText={alertConfig.cancelText}
      />
    <section className="py-20 bg-gradient-to-b from-white via-purple-50/30 to-amber-50/20">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">

          {/* ── En-tête de section ── */}
          <div className="text-center mb-14">
            <h2 className="text-4xl md:text-5xl font-light text-purple-900 mb-3">
              Ils nous font confiance
            </h2>
            <p className="text-gray-500 max-w-xl mx-auto">
              Découvrez les expériences vécues par nos clientes au sein d'Alaia Cristal Bien-être
            </p>
          </div>

          {/* ── Bandeau de moyenne ── */}
          {!loading && reviews.length > 0 && (
            <div className="mb-12">
              <div className="bg-white rounded-3xl shadow-xl border border-purple-100 p-8 max-w-3xl mx-auto">
                <div className="flex flex-col md:flex-row items-center gap-8">

                  {/* Score central */}
                  <div className="flex flex-col items-center flex-shrink-0">
                    <span className="text-7xl font-light text-purple-900 leading-none">{average}</span>
                    <span className="text-gray-400 text-sm mt-1">sur 5</span>
                    <StarRating value={Math.round(parseFloat(average))} readonly size="md" />
                    <p className="text-sm text-gray-500 mt-2">
                      {reviews.length} avis {reviews.length > 1 ? 'clients' : 'client'}
                    </p>
                  </div>

                  {/* Séparateur */}
                  <div className="w-px h-28 bg-purple-100 hidden md:block flex-shrink-0"></div>

                  {/* Barres de répartition */}
                  <div className="flex-1 w-full space-y-2">
                    {ratingCounts.map(({ star, count }) => {
                      const pct = reviews.length ? Math.round((count / reviews.length) * 100) : 0;
                      return (
                        <div key={star} className="flex items-center gap-3">
                          <span className="text-xs font-medium text-gray-500 w-4 text-right">{star}</span>
                          <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400 flex-shrink-0" />
                          <div className="flex-1 bg-gray-100 rounded-full h-2 overflow-hidden">
                            <div
                              className="h-2 rounded-full bg-gradient-to-r from-amber-400 to-amber-500 transition-all duration-700"
                              style={{ width: `${pct}%` }}
                            />
                          </div>
                          <span className="text-xs text-gray-400 w-8">{count}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── Grille des avis ── */}
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block w-8 h-8 border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin"></div>
              <p className="text-gray-400 mt-3">Chargement des avis…</p>
            </div>
          ) : reviews.length === 0 ? (
            <div className="text-center py-16">
              <MessageCircle className="w-16 h-16 text-purple-200 mx-auto mb-4" />
              <p className="text-gray-500 text-lg">Soyez la première à partager votre expérience !</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
                {visibleReviews.map((review) => (
                  <ReviewCard key={review.id} review={review} />
                ))}
              </div>

              {/* Bouton voir plus / moins */}
              {reviews.length > REVIEWS_PREVIEW && (
                <div className="text-center mt-4 mb-8">
                  <button
                    onClick={() => setShowAll(!showAll)}
                    className="inline-flex items-center gap-2 text-purple-600 hover:text-purple-800 font-medium transition-colors"
                  >
                    {showAll ? (
                      <><ChevronUp className="w-4 h-4" /> Voir moins</>
                    ) : (
                      <><ChevronDown className="w-4 h-4" /> Voir les {reviews.length - REVIEWS_PREVIEW} autres avis</>
                    )}
                  </button>
                </div>
              )}
            </>
          )}

          {/* ── Message de succès ── */}
          {submitSuccess && (
            <div className="max-w-xl mx-auto mb-8 bg-green-50 border border-green-200 text-green-700 rounded-2xl px-6 py-4 text-center text-sm font-medium shadow">
              Merci pour votre avis ! Il a bien été publié.
            </div>
          )}

          {/* ── Zone formulaire ── */}
          <div className="max-w-xl mx-auto mt-6">
            {!showForm ? (
              <div className="text-center">
                {userHasReviewed ? (
                  <p className="text-gray-400 text-sm italic">Vous avez déjà partagé votre avis. Merci ! 💜</p>
                ) : (
                  <button
                    onClick={() => {
                      if (!user) {
                        error('Veuillez vous connecter pour laisser un avis.');
                        return;
                      }
                      setShowForm(true);
                    }}
                    className="group inline-flex items-center gap-2 bg-gradient-to-br from-purple-500 to-pink-500 text-white px-8 py-4 rounded-full font-medium shadow-xl hover:shadow-2xl hover:shadow-purple-500/40 transition-all duration-300 transform hover:scale-105"
                  >
                    <MessageCircle className="w-5 h-5" />
                    Laisser un avis
                    <Sparkles className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </button>
                )}
              </div>
            ) : (
              <div className="bg-white rounded-3xl shadow-2xl border border-purple-100 overflow-hidden">
                {/* Header du formulaire */}
                <div className="bg-gradient-to-r from-purple-600 to-pink-600 px-8 py-5">
                  <h3 className="text-white font-semibold text-lg">Partagez votre expérience</h3>
                  <p className="text-purple-100 text-sm mt-0.5">Votre avis aide les autres clients</p>
                </div>

                <form onSubmit={handleSubmit} className="p-8 space-y-6">
                  {/* Note */}
                  <div>
                    <label className="block text-sm font-semibold text-purple-900 mb-3">
                      Votre note *
                    </label>
                    <StarRating
                      value={form.rating}
                      onChange={(v) => setForm(p => ({ ...p, rating: v }))}
                      size="lg"
                    />
                    {form.rating > 0 && (
                      <p className="text-xs text-gray-400 mt-2">
                        {['', 'Décevant', 'Peut mieux faire', 'Bien', 'Très bien', 'Excellent !'][form.rating]}
                      </p>
                    )}
                  </div>

                  {/* Soin (optionnel) */}
                  <div>
                    <label className="block text-sm font-semibold text-purple-900 mb-2">
                      Soin réalisé <span className="font-normal text-gray-400">(optionnel)</span>
                    </label>
                    <input
                      type="text"
                      value={form.serviceName}
                      onChange={(e) => setForm(p => ({ ...p, serviceName: e.target.value }))}
                      placeholder="Ex : Massage Lomi Lomi, Soin visage…"
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-purple-400 focus:outline-none focus:ring-2 focus:ring-purple-100 text-sm transition"
                    />
                  </div>

                  {/* Commentaire */}
                  <div>
                    <label className="block text-sm font-semibold text-purple-900 mb-2">
                      Votre commentaire *
                    </label>
                    <textarea
                      value={form.comment}
                      onChange={(e) => setForm(p => ({ ...p, comment: e.target.value }))}
                      placeholder="Décrivez votre expérience, l'ambiance, les bienfaits ressentis…"
                      rows={4}
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-purple-400 focus:outline-none focus:ring-2 focus:ring-purple-100 text-sm resize-none transition"
                    />
                    <p className="text-xs text-gray-400 mt-1 text-right">{form.comment.length}/500</p>
                  </div>

                  {/* Erreur */}
                  {formError && (
                    <p className="text-red-500 text-sm bg-red-50 rounded-xl px-4 py-3">{formError}</p>
                  )}

                  {/* Actions */}
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => { setShowForm(false); setFormError(''); setForm({ rating: 0, comment: '', serviceName: '' }); }}
                      className="flex-1 px-5 py-3 rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-50 transition font-medium text-sm"
                    >
                      Annuler
                    </button>
                    <button
                      type="submit"
                      disabled={submitting}
                      className={`flex-1 flex items-center justify-center gap-2 px-5 py-3 rounded-xl font-medium text-sm text-white shadow-lg transition-all duration-300 ${
                        submitting
                          ? 'bg-gray-400 cursor-not-allowed'
                          : 'bg-gradient-to-r from-purple-600 to-pink-600 hover:shadow-purple-500/40 hover:scale-[1.02]'
                      }`}
                    >
                      <Send className="w-4 h-4" />
                      {submitting ? 'Publication…' : 'Publier mon avis'}
                    </button>
                  </div>
                </form>
              </div>
            )}
          </div>

        </div>
      </div>
    </section>
    </>
  );
};

export default ReviewSystem;